import { refreshToken } from "@/features/auth/api";
import { message, notification } from "antd";
import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import i18next from "i18next";
import { useAuthStore } from "../store/useAuthStore";
import type { IErrorResponse } from "../types";

const hostname = typeof window !== "undefined" ? window.location.hostname : "";

let refreshPromise: Promise<any> | null = null;
const clearPromise = () => (refreshPromise = null);

export const baseURL = import.meta.env.VITE_API_URL;

const request = axios.create({
  baseURL,
  withCredentials: true,
});

request.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // "if url doesn't end with a slash, server will return 404"
  // if (config?.url?.[config?.url?.length - 1] !== "/") {
  //   config.url = `${config.url}/`;
  // }

  const locale =
    (i18next.language === "oz" ? "uz-latin" : i18next.language) || "ru";

  config.headers["Accept-Language"] = locale;

  return config;
});

request.interceptors.response.use(
  (response) => response.data,
  async (error: AxiosError<IErrorResponse[]>) => {
    const { status } = error;
    const config = error?.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    if ((status === 403 || status === 401) && !config?._retry) {
      config._retry = true;
      const rToken = localStorage.getItem("refresh_token");
      if (rToken && rToken !== "undefined" && !refreshPromise) {
        refreshPromise = refreshToken({ refresh: rToken })
          .catch(() => {
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            useAuthStore.setState({ isAuth: false, user: null });
            message.info(i18next.t("auth.login-again"));
          })
          .finally(clearPromise);
        const refreshedData = await refreshPromise;
        if (refreshedData) {
          const { access, refresh } = refreshedData;
          localStorage.setItem("access_token", access);
          localStorage.setItem("refresh_token", refresh);
        }
      } else {
        throw new Error("No refresh token found");
      }
    } else {
      error.response?.data.forEach((err) => {
        notification.error({
          message: i18next.t("error.unexpected"),
          description: err?.detail,
        });
      });
    }
    return Promise.reject(error);
  },
);

export default request;
